import React, { useMemo } from "react";
import { useTable, useSortBy, usePagination, useFilters } from "react-table";
import { Table, Form, Button } from "react-bootstrap";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

// A simple text filter for columns
const DefaultColumnFilter = ({ column: { filterValue, setFilter } }: any) => (
    <Form.Control
        size="sm"
        type="text"
        placeholder="Filtrele..."
        value={filterValue || ""}
        onChange={(e) => setFilter(e.target.value || undefined)}
    />
);

const ActivityLogTable = ({ data }: { data: any[] }) => {
    const { t } = useTranslation();

    const columns = useMemo(
        () => [
            {
                Header: t("Receipt.date"),
                accessor: "timestamp",
                Filter: DefaultColumnFilter,
                Cell: ({ row }: any) => {
                    const dateValue = row.original.date || row.original.timestamp;
                    if (!dateValue) return 'N/A';
                    try {
                        // Handle both ISO string and timestamp number
                        const date = typeof dateValue === 'number' 
                            ? new Date(dateValue) 
                            : new Date(dateValue);
                        return date.toLocaleString("tr-TR");
                    } catch {
                        return dateValue;
                    }
                }
            },
            {
                Header: t("Profile.name"),
                accessor: "userName",
                Filter: DefaultColumnFilter,
            },
            {
                Header: t("Profile.role"),
                accessor: "role",
                Filter: DefaultColumnFilter,
                Cell: ({value}: any) => t(`ActivityLogs.${value}`)
            },
            {
                Header: t("Alerts.action"),
                accessor: "action",
                Filter: DefaultColumnFilter,
                Cell: ({value}: any) => t(`ActivityLogs.${value}`)
            }
        ],
        [t]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        nextPage,
        previousPage,
        pageOptions,
        gotoPage,
        state: { pageIndex },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageSize: 20 },
            defaultColumn: { Filter: DefaultColumnFilter },
        },
        useFilters,
        useSortBy,
        usePagination
    );

    return (
        <div>
            <Table bordered hover responsive size="sm" {...getTableProps()}>
                <thead>
                {headerGroups.map((headerGroup: any) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column: any) => (
                            <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                {column.render("Header")}
                                {column.isSorted ? (
                                    column.isSortedDesc ? (
                                        <ArrowDownward fontSize="small" />
                                    ) : (
                                        <ArrowUpward fontSize="small" />
                                    )
                                ) : null}
                                <div style={{ marginTop: 4 }}>{column.canFilter ? column.render("Filter") : null}</div>
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                {page.map((row: any) => {
                    prepareRow(row);
                    return (
                        <tr {...row.getRowProps()}>
                            {row.cells.map((cell: any) => (
                                <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                            ))}
                        </tr>
                    );
                })}
                </tbody>
            </Table>

            <div className="d-flex justify-content-between align-items-center mt-2">
                <div>
                    <strong>{pageIndex + 1}</strong> / <strong>{pageOptions.length}</strong>
                </div>
                <div>
                    <Button variant="dark" size="sm" onClick={() => previousPage()} disabled={!canPreviousPage}>
                        {t('Alerts.previous')}
                    </Button>{" "}
                    <Button variant="primary" size="sm" onClick={() => nextPage()} disabled={!canNextPage}>
                        {t('Alerts.next')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ActivityLogTable;
